import classNames from "classnames";
import { FC, useMemo } from "react";
import { LuCheck } from "react-icons/lu";

import { useGetWorkTypeQuery } from "../../../slices/WorkTypeSlice";
import {
  CurrentStatus,
  currentStatusItemKey,
  currentStatusOutsideList,
  statusChangeItems,
} from "../board/worksBoard.utils";

type Props = {
  workTypeId: string;
  setIsOpen: (value: boolean) => void;
  updateStatusWork: (value: {
    statusId: string | null;
    subStatusId: string | null;
  }) => void;
  /** The work's status now (marked with a check). */
  current?: CurrentStatus;
};

/**
 * Status changer in the look of the Work page "Select status" list (Roadmap
 * 1858): main statuses bold at the left, sub-statuses indented, colored dots,
 * same font and size; one click changes the status (no counts, no checkboxes).
 */
export const StatusMenuList: FC<Props> = ({
  workTypeId,
  setIsOpen,
  updateStatusWork,
  current,
}) => {
  const { data, isLoading } = useGetWorkTypeQuery({ id: workTypeId });
  const items = useMemo(
    () => statusChangeItems(data?.serviceStatuses ?? []),
    [data?.serviceStatuses],
  );
  const currentKey = useMemo(
    () => currentStatusItemKey(items, current),
    [items, current],
  );
  // A status the work type doesn't have (any more): shown first, ticked.
  const outsideLabel = useMemo(
    () => currentStatusOutsideList(items, current),
    [items, current],
  );

  return (
    <div
      className={
        "max-h-[340px] min-w-[210px] overflow-y-auto rounded-[10px] border border-[#E5E7EB] bg-white p-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.12)]"
      }>
      {isLoading ? (
        <div className={"px-2.5 py-2 text-[12.5px] font-medium text-[#9097A8]"}>
          Loading…
        </div>
      ) : !items.length ? (
        <div className={"px-2.5 py-2 text-[12.5px] font-medium text-[#9097A8]"}>
          No statuses
        </div>
      ) : (
        <>
          {outsideLabel && (
            <div
              aria-current={true}
              title={"This status is not in this work type"}
              className={
                "mb-[3px] flex w-full cursor-default items-center gap-2.5 whitespace-nowrap rounded-[7px] border-b border-[#EEF0F3] bg-[#F7F8FA] px-2.5 py-2 text-left text-[12.5px] font-semibold text-[#9097A8]"
              }>
              <span
                className={"h-[9px] w-[9px] shrink-0 rounded-full bg-[#C5CAD3]"}
              />
              <span className={"flex flex-1 flex-col"}>
                <span>{outsideLabel}</span>
                <span className={"text-[11px] font-medium text-[#A0A6B4]"}>
                  Not in this work type
                </span>
              </span>
              <LuCheck
                size={13}
                strokeWidth={2.8}
                className={"ml-3 shrink-0 text-[#9097A8]"}
              />
            </div>
          )}
          {items.map((item) => {
            const isCurrent = item.key === currentKey;
            return (
              <button
                key={item.key}
                type={"button"}
                aria-current={isCurrent || undefined}
                onClick={(event) => {
                  event.stopPropagation();
                  setIsOpen(false);
                  if (isCurrent) return;
                  updateStatusWork({
                    statusId: item.statusId,
                    subStatusId: item.subStatusId,
                  });
                }}
                className={classNames(
                  "flex w-full items-center gap-2.5 whitespace-nowrap rounded-[7px] px-2.5 py-2 text-left text-[12.5px] font-semibold text-[#3A4256] hover:bg-[#F5F6F8]",
                  isCurrent && "bg-[#F4F2FE]",
                )}>
                <span
                  className={classNames(
                    "h-[9px] w-[9px] shrink-0 rounded-full",
                    !item.isGroup && "ml-3",
                  )}
                  style={{ backgroundColor: item.color }}
                />
                <span
                  className={classNames(
                    "flex-1",
                    item.isGroup && "font-extrabold text-[#1F2433]",
                  )}>
                  {item.label}
                </span>
                {isCurrent && (
                  <LuCheck
                    size={13}
                    strokeWidth={2.8}
                    className={"ml-3 shrink-0 text-[#6B4EFF]"}
                  />
                )}
              </button>
            );
          })}
        </>
      )}
    </div>
  );
};
